import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getUser, roleDisplayLabel } from "@/lib/auth";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, MapPin } from "lucide-react";
import { formatINR, formatNum } from "@/lib/format";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// fix default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const redIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
  className: "hue-rotate-marker",
});

export default function SupervisorHome() {
  const user = getUser();
  // ITEM 7 re-audit (2026-02-29) — subtitle must reflect the logged-in user's role
  // (Co-ordinator for Supervisor/IT, Manager for Project Lead). Was hardcoded "Co-ordinator".
  const roleLabel = roleDisplayLabel(user?.role);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/dashboard/stores"),
        api.get("/supervisor/pending"),
      ]);
      setRows(r.data); setPending(p.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (sale_id) => {
    try {
      await api.patch(`/sales/${sale_id}/approve`);
      toast.success("Approved");
      load();
    } catch { toast.error("Failed"); }
  };

  const center = rows.length ? [rows[0].gps_lat, rows[0].gps_lng] : [26.8467, 80.9462];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="My Stores" subtitle={`${roleLabel} · assigned stores + approvals`} />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2 rounded-xl border-gray-200 bg-white overflow-hidden" data-testid="supervisor-map-card">
              <div className="p-4 border-b border-gray-100">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Map view</div>
                <div className="font-display text-lg font-bold text-gray-900">Your {rows.length} stores</div>
              </div>
              <div style={{ height: 420 }}>
                <MapContainer center={center} zoom={7} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {rows.map(r => (
                    <Marker key={r.store_id} position={[r.gps_lat, r.gps_lng]} icon={redIcon}>
                      <Popup>
                        <div className="text-sm">
                          <div className="font-semibold">{r.store_name}</div>
                          <div className="text-xs text-gray-500">{r.store_code} · {r.city}</div>
                          <div className="mt-1">Cups: {formatNum(r.glasses_sold)} ({r.target_pct}%)</div>
                          <div>Sales: {formatINR(r.sales)}</div>
                          <div>Attendance: {r.attendance}</div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </Card>

            <Card className="rounded-xl border-gray-200 bg-white overflow-hidden" data-testid="approval-queue-card">
              <div className="p-4 border-b border-gray-100">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Approval queue</div>
                <div className="font-display text-lg font-bold text-gray-900">{pending.length} pending</div>
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                {pending.map(p => (
                  <div key={p.id} className="p-3 border-b border-gray-100" data-testid={`pending-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{p.store_name}</div>
                        <div className="text-xs text-gray-500">{p.date} · {p.promoter_name}</div>
                        <div className="text-sm mt-1">
                          <span className="font-semibold">{formatNum(p.glasses_sold)}</span> cups · {formatINR(p.total_sales)}
                          {p.payment_check_flag && <Badge variant="destructive" className="ml-2 text-[10px]">Mismatch</Badge>}
                        </div>
                      </div>
                      <Button data-testid={`approve-${p.id}`} size="sm" onClick={() => approve(p.id)} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                ))}
                {pending.length === 0 && <div className="p-8 text-center text-gray-500 text-sm">All caught up</div>}
              </div>
            </Card>
          </div>

          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Assigned stores</div>
              <div className="font-display text-lg font-bold text-gray-900">Today status</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="supervisor-stores-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Store</th>
                    <th className="text-left px-4 py-3">Promoter</th>
                    <th className="text-right px-4 py-3">Cups</th>
                    <th className="text-right px-4 py-3">Sales</th>
                    <th className="text-right px-4 py-3">Target %</th>
                    <th className="text-left px-4 py-3">Attendance</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.store_id} className="border-t border-gray-100" data-testid={`sup-row-${r.store_no}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.store_name}</div>
                        <div className="text-xs text-gray-500">{r.city}</div>
                      </td>
                      <td className="px-4 py-3">{r.promoter}</td>
                      <td className="px-4 py-3 text-right">{formatNum(r.glasses_sold)}</td>
                      <td className="px-4 py-3 text-right">{formatINR(r.sales)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${r.target_pct>=80?"text-green-700":r.target_pct>=50?"text-amber-700":"text-red-700"}`}>{r.target_pct}%</span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={
                          r.attendance === "Present" ? "border-green-300 text-green-700 bg-green-50" :
                          r.attendance === "Late" ? "border-amber-300 text-amber-700 bg-amber-50" :
                          "border-gray-300 text-gray-500 bg-gray-50"
                        }>{r.attendance}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {!r.entered ? <Badge variant="outline" className="border-gray-300 text-gray-500">No entry</Badge>
                          : r.approved ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Approved</Badge>
                          : <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Pending</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </main>
      </div>
    </div>
  );
}
